import { useState } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Button, Chip, Popover, PopoverContent, PopoverTrigger } from '@nextui-org/react'

import { useAppDispatch, useAppSelector, useQueryScreen } from '@/hooks'
import { settingsActions } from '@/redux/reducers/settings/setting.reducer'
import ICONS from '@/assets/icons'
import { NormalMenuType } from '@/components/layouts/constants'
import { cn } from '@/utils/cn'
import { useGetNotificationQuery } from '@/redux/api/notification'
import { ParamsFilterNoti } from '@/redux/api/notification/notification.type'

import styles from '../../styles.module.css'

export const ItemMenuType = ({ item }: { item: NormalMenuType & { path: string } }) => {
  const pathname = usePathname()
  const dispatch = useAppDispatch()
  const isPc = useQueryScreen('pc')

  const user = useAppSelector((state) => state.root.auth.user)

  // const isDisabled = !user?.walletAddress && item.path === PATHS.MESSAGE;
  const isDisabled = !user?.walletAddress && item.path === 'test'

  const isActive = pathname.includes(item.path)

  const [isOpenLock, setIsOpenLock] = useState<boolean>(false)

  if (isDisabled)
    return (
      <Popover
        placement={isPc ? 'right' : 'bottom'}
        showArrow
        isOpen={isOpenLock}
        onOpenChange={(isOpen) => setIsOpenLock(isOpen)}
      >
        <PopoverTrigger>
          <Link
            prefetch={true}
            href={item.path}
            className={cn('flex items-center gap-3 rounded-lg px-3 py-2.5 text-ss-grayscale-100', {
              'border border-solid': isOpenLock,
            })}
            onClick={(e) => {
              e.preventDefault()
            }}
          >
            <Image
              src={item.icon}
              alt={item.label}
              width={24}
              height={24}
              // className={'opacity-40'}
              loading="lazy"
            />
            <p className="text-sm font-ibm font-medium">{item.label}</p>
            <Image
              src={isOpenLock ? ICONS.lockActive : ICONS.lock}
              alt={item.label}
              width={24}
              height={24}
              className={'ml-8'}
              loading="lazy"
            />
          </Link>
        </PopoverTrigger>
        <PopoverContent className="rounded-md p-0">
          <div className="flex max-w-[90vw] flex-col gap-4 p-6 lg:max-w-[500px]">
            <Chip
              className="bg-gradient px-5"
              startContent={<Image alt="" width={20} height={20} src={ICONS.Unlocked} />}
              classNames={{
                base: 'flex item-center w-fit gap-2 px-1 py-4',
                content: 'font-semibold text-[14px] leading-[14px] p-0',
              }}
            >
              Unlock Message Feature
            </Chip>

            <div>
              <h3 className="text-base font-medium text-black">Create wallet to unlock this feature</h3>
              <p className="text-sm text-[#808191]">
                Create your own Sightsea in-app wallet and deposit fund to unlock this feature and chat directly with
                awesome friends on our platform.
              </p>
            </div>

            <Button
              variant="flat"
              color="secondary"
              className="flex w-fit cursor-pointer items-center gap-1 bg-[#6C5DD3]/10 text-[12px] font-semibold text-secondary"
              as={Link}
              onClick={() => setIsOpenLock(false)}
              href="/create-confirm-pass"
            >
              <Image
                src={ICONS.walletMessage}
                alt=""
                width={20}
                height={20}
                className="h-[20px] w-[20px] object-cover"
              />
              <span className="text-[12px] font-semibold">Create Wallet</span>
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    )

  return (
    <Link
      prefetch={true}
      href={item.path}
      target={item.label.toLowerCase().includes('tutorial') ? '_blank' : '_self'}
      rel="noopener noreferrer"
      className={cn('flex items-center gap-3 rounded-lg py-2.5 lg:py-3 text-ss-grayscale-100', {
        'ss-text-linear': isActive,
      })}
      onClick={() => {
        if (!isPc) dispatch(settingsActions.setMenu(false))
      }}
    >
      <Image
        src={isActive && !isDisabled ? item.active : item.icon}
        alt={item.label}
        width={24}
        height={24}
        className={cn(styles.iconMenu, 'w-5 h-5 md:w-6 md:h-6')}
        loading="lazy"
      />
      <p className="text-sm lg:text-base font-ibm font-medium">{item.label}</p>
      <RenderCountNoti path={item.path} />
    </Link>
  )
}

const RenderCountNoti = ({ path }: { path: string }) => {
  const params = {
    page: 0,
    limit: 1,
    sort: 'DESC',
    type: undefined,
    isSeen: false,
  } as ParamsFilterNoti
  const { data } = useGetNotificationQuery(params, { skip: path !== '/notifications' })

  switch (path) {
    case '/notifications': {
      if (Number(data?.meta?.totalAllPageItemCount) > 0) {
        return (
          <Chip
            size="sm"
            style={{ WebkitTextFillColor: '#6941C6' }}
            classNames={{
              base: 'border-[#9E77ED] ml-auto bg-[#D6BBFB]',
              content: 'text-ss-purple-70 font-medium text-xs',
            }}
            variant="bordered"
          >
            <span className="text-ss-purple-70">{Number(data?.meta?.totalAllPageItemCount)}</span>
          </Chip>
        )
      }
      return null
    }

    default:
      return null
  }
}
